// src/forces/SemanticForceEngine.ts
// A lightweight d3-force compatible engine that applies:
// - Global gravity toward origin
// - Pairwise short-range repulsion
// - Sparse semantic affinities (attraction toward a small set of neighbors)
// - Temperature (small random velocity noise)
//
// Designed to be used with react-force-graph-3d via fg.d3Force('semantic', ...)

export type SemanticAffinityMap = Record<string, number>;

export type SemanticForceNode = {
  id: string;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  mass?: number;
  affinity?: SemanticAffinityMap;
  isAnchor?: boolean;
};

export type SemanticForceConfig = {
  /** Global gravity strength (toward origin). */
  G: number;
  /** Repulsion strength. */
  K: number;
  /** Random jitter magnitude (added to velocity per tick). */
  temperature: number;
  /** Maximum distance at which repulsion is applied (units). */
  repulsionCutoff?: number;
  /** Softening to avoid singularities. */
  epsilon?: number;
  /** Clamp acceleration per tick to prevent instability. */
  maxAccel?: number;
};

export function createSemanticForceEngine(initial: Partial<SemanticForceConfig> = {}) {
  let nodes: SemanticForceNode[] = [];
  let nodeById: Map<string, SemanticForceNode> = new Map();

  const cfg: SemanticForceConfig = {
    G: initial.G ?? 25,
    K: initial.K ?? 200,
    temperature: initial.temperature ?? 0.02,
    repulsionCutoff: initial.repulsionCutoff ?? 260,
    epsilon: initial.epsilon ?? 0.0001,
    maxAccel: initial.maxAccel ?? 8,
  };

  let lastMs = 0;
  let avgMs = 0;

  function force(alpha: number) {
    if (!nodes.length) return;

    const t0 = performance.now();

    const G = cfg.G;
    const K = cfg.K;
    const temp = cfg.temperature;
    const cutoff = cfg.repulsionCutoff ?? 260;
    const cutoff2 = cutoff * cutoff;
    const eps = cfg.epsilon ?? 0.0001;
    const maxAccel = cfg.maxAccel ?? 8;

    // 1) Global gravity toward origin (keeps the scene cohesive)
    for (const n of nodes) {
      if (n.isAnchor) continue;

      const m = n.mass ?? 1;
      const x = n.x || 0;
      const y = n.y || 0;
      const z = n.z || 0;

      const r2 = x * x + y * y + z * z + eps;
      const r = Math.sqrt(r2);

      // Acceleration toward origin; scale by alpha so it cools down.
      const a = (G * alpha) / r;

      n.vx += (-x * a) / Math.max(1, m);
      n.vy += (-y * a) / Math.max(1, m);
      n.vz += (-z * a) / Math.max(1, m);
    }

    // 2) Short-range repulsion (naive O(N^2) with cutoff; intended for macro nodes only)
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i];
      if (a.isAnchor) continue;
      for (let j = i + 1; j < nodes.length; j++) {
        const b = nodes[j];
        if (b.isAnchor) continue;

        const dx = (a.x || 0) - (b.x || 0);
        const dy = (a.y || 0) - (b.y || 0);
        const dz = (a.z || 0) - (b.z || 0);

        const d2 = dx * dx + dy * dy + dz * dz + eps;
        if (d2 > cutoff2) continue;

        const d = Math.sqrt(d2);
        const mag = (K * alpha) / d2; // inverse-square

        const ax = (dx / d) * mag;
        const ay = (dy / d) * mag;
        const az = (dz / d) * mag;

        const am = Math.max(1, a.mass ?? 1);
        const bm = Math.max(1, b.mass ?? 1);

        a.vx += ax / am;
        a.vy += ay / am;
        a.vz += az / am;

        b.vx -= ax / bm;
        b.vy -= ay / bm;
        b.vz -= az / bm;
      }
    }

    // 3) Sparse semantic affinities: pull nodes toward a small set of neighbors.
    // We treat affinities as attraction proportional to similarity.
    for (const n of nodes) {
      if (n.isAnchor) continue;
      const aff = n.affinity;
      if (!aff) continue;

      const nm = Math.max(1, n.mass ?? 1);

      for (const [targetId, wRaw] of Object.entries(aff)) {
        const t = nodeById.get(targetId);
        if (!t) continue;

        const w = Math.max(0, Number(wRaw) || 0);
        if (w <= 0) continue;

        const dx = (t.x || 0) - (n.x || 0);
        const dy = (t.y || 0) - (n.y || 0);
        const dz = (t.z || 0) - (n.z || 0);

        const d2 = dx * dx + dy * dy + dz * dz + eps;
        const d = Math.sqrt(d2);

        // Similarity-weighted attraction. A mild "spring" feel: grows with distance, damped by 1/d.
        const pull = w * G * alpha;
        const ax = (dx / d) * pull;
        const ay = (dy / d) * pull;
        const az = (dz / d) * pull;

        n.vx += ax / nm;
        n.vy += ay / nm;
        n.vz += az / nm;

        // If the target isn't an anchor, apply a smaller reciprocal pull for stability.
        if (!t.isAnchor) {
          const tm = Math.max(1, t.mass ?? 1);
          t.vx -= (ax * 0.35) / tm;
          t.vy -= (ay * 0.35) / tm;
          t.vz -= (az * 0.35) / tm;
        }
      }
    }

    // 4) Temperature: small random jitter (kept tiny by default)
    if (temp > 0) {
      const s = temp * alpha;
      for (const n of nodes) {
        if (n.isAnchor) continue;
        n.vx += (Math.random() - 0.5) * s;
        n.vy += (Math.random() - 0.5) * s;
        n.vz += (Math.random() - 0.5) * s;
      }
    }

    // Clamp per-tick velocity changes to avoid blow-ups.
    for (const n of nodes) {
      if (n.isAnchor) continue;
      const vx = n.vx || 0;
      const vy = n.vy || 0;
      const vz = n.vz || 0;
      const v = Math.sqrt(vx * vx + vy * vy + vz * vz);
      if (v > maxAccel) {
        const k = maxAccel / v;
        n.vx = vx * k;
        n.vy = vy * k;
        n.vz = vz * k;
      }
    }

    // perf telemetry
    lastMs = performance.now() - t0;
    avgMs = avgMs ? avgMs * 0.9 + lastMs * 0.1 : lastMs;
    (force as any).__lastMs = lastMs;
    (force as any).__avgMs = avgMs;
  }

  force.initialize = (ns: any[]) => {
    nodes = (ns || []) as SemanticForceNode[];
    nodeById = new Map(nodes.map((n) => [String(n.id), n]));
  };

  // d3-force style setters
  (force as any).G = (v?: number) => {
    if (v === undefined) return cfg.G;
    cfg.G = v;
    return force;
  };
  (force as any).K = (v?: number) => {
    if (v === undefined) return cfg.K;
    cfg.K = v;
    return force;
  };
  (force as any).temperature = (v?: number) => {
    if (v === undefined) return cfg.temperature;
    cfg.temperature = v;
    return force;
  };

  return force as any;
}
